import "./index.css";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import * as Sentry from "@sentry/react";
import { SnackbarProvider } from "notistack";
import { WebStorageStateStore } from "oidc-client-ts";
import ReactDOM from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { AuthProvider } from "react-oidc-context";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { BrowserRouter } from "react-router-dom";

import App from "./App";
import ErrorBoundaryFallback from "./components/ErrorBoundaryFallback";
import { Config } from "./config";
import { stripOidcQueryParamsFromHref } from "./helpers/routes";
import reportWebVitals from "./reportWebVitals";
import theme from "./theme";

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

if (process.env.NODE_ENV === "production") {
  Sentry.init({
    dsn: "https://5760c4d51b1373dc3e8138a1ac825e91@o4507268658364416.ingest.us.sentry.io/4507268665835520",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      /^https:\/\/auto-pilot\.auto-ux\.io/,
      /^https:\/\/internal-auto-pilot\.auto-ux\.io/,
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

console.log("Keycloak Url:", Config.keycloakUrl);
console.log("Keycloak Realm:", Config.keycloakRealm);
console.log("App Scope:", Config.applicationScope);
console.log("API Url:", Config.apiUrl);
console.log("Env:", Config.env);

const oidcConfig = {
  authority: `${Config.keycloakUrl}/realms/${
    Config.keycloakRealm || "auto-pilot"
  }`,
  client_id: "auto-pilot-portal",
  redirect_uri: stripOidcQueryParamsFromHref(window.location.href),
  response_type: "code", // Standard Authorization Code Flow
  scope: "openid profile email", // Standard OIDC scopes
  monitorSession: true, // Enables the session check iframe
  accessTokenExpiringNotificationTimeInSeconds: 30,
  silent_redirect_uri: `${window.location.origin}/silent-check-sso.html`,
  /** Renew access token in a hidden iframe before expiry (uses silent_redirect_uri). */
  automaticSilentRenew: true,
  silentRequestTimeoutInSeconds: 30,
  userStore: new WebStorageStateStore({
    store: window.localStorage,
  }),
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
      retry: 0,
    },
  },
});

function AppWrapper() {
  return (
    <AuthProvider {...oidcConfig}>
      <QueryClientProvider client={queryClient}>
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={
            Config.applicationScope === "internal"
              ? { horizontal: "right", vertical: "top" }
              : { horizontal: "center", vertical: "bottom" }
          }
        >
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <BrowserRouter>
                  <App />
                </BrowserRouter>
              </LocalizationProvider>
            </ErrorBoundary>
          </ThemeProvider>
          <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
        </SnackbarProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
}

root.render(<AppWrapper></AppWrapper>);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
